import { DEFAULT_LOCALE } from './util/locale'

interface LanguagesRemoteConfigLanguage {
  locale: string
  nativeName?: string
}

interface LanguagesRemoteConfig {
  visibleLanguages?: Array<LanguagesRemoteConfigLanguage>
  preReleaseLanguages?: Array<LanguagesRemoteConfigLanguage>
}

export interface VisibleLanguageOption {
  locale: string
  /** Language name in its native language */
  nativeName: string
}

function isLanguagesRemoteConfigValue(
  value: unknown,
): value is LanguagesRemoteConfig {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isLanguagesRemoteConfigLanguage(
  value: unknown,
): value is LanguagesRemoteConfigLanguage {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'locale' in value &&
    typeof (value as Record<string, unknown>).locale === 'string'
  )
}

export function getVisibleLanguageOptions(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<VisibleLanguageOption> {
  if (remoteConfig?.error) {
    return []
  }

  const { value } = remoteConfig ?? {}
  if (!isLanguagesRemoteConfigValue(value)) {
    return []
  }

  if (!Array.isArray(value.visibleLanguages)) {
    return []
  }

  const addedLocales = new Set<string>()
  return value.visibleLanguages.reduce<Array<VisibleLanguageOption>>(
    (result, languageConfig) => {
      if (!isLanguagesRemoteConfigLanguage(languageConfig)) {
        return result
      }

      const locale = languageConfig.locale.trim()
      if (!locale || addedLocales.has(locale)) {
        return result
      }

      addedLocales.add(locale)
      const { nativeName } = languageConfig
      const configuredNativeName =
        typeof nativeName === 'string' ? nativeName.trim() : undefined

      result.push({
        locale,
        nativeName: configuredNativeName || locale,
      })
      return result
    },
    [],
  )
}

export function getVisibleLanguageLocales(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<string> {
  return getVisibleLanguageOptions(remoteConfig).map(({ locale }) => locale)
}

export function getEnabledLanguageLocales(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<string> {
  const visibleLocales = getVisibleLanguageLocales(remoteConfig)
  return [
    DEFAULT_LOCALE,
    ...visibleLocales.filter((locale) => locale !== DEFAULT_LOCALE),
  ]
}

export function isVisibleLanguageLocale(
  remoteConfig: App.LabRemoteConfigResult | undefined,
  locale: string,
): boolean {
  return getVisibleLanguageOptions(remoteConfig).some(
    (option) => option.locale === locale,
  )
}
